import { useEffect, useRef, useState } from "react";
import ChevronDownIconDark from "../assets/li_chevron-down-dark.svg";
import ChevronDownIcon from "../assets/li_chevron-down.svg";
import { useMainContext } from '../context/MainContext.tsx';

import { useScrollToError } from '../hooks/useScrollToError.ts';
import { nonSpectreJourney } from '../utils/backgroundImage.ts';
import { errorClasses, getInputClasses } from "../utils/inputStyles";
import FieldValidationMessage from "./FieldValidationMessage.tsx";

type DataSourceItem = {
  key?: string;
  value?: string;
};

type IProps = {
  updateFieldValue: (value: string) => void;
  triggerFieldChange: (value: string) => void;
  label: string;
  options: DataSourceItem[];
  validatemessage?: string;
  value?: string;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  name?: string;
  className?: string;
  showFlags?: boolean;
  fullWidth?: boolean;
};

function Select(props: IProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { journeyId } = useMainContext();
  const rootRef = useScrollToError(props.validatemessage, [props.value, props.updateFieldValue]);
  const [selectedOption, setSelectedOption] = useState<DataSourceItem | null>(
    null
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Import flag images
  const usFlag = new URL("../assets/flags-svg/us.svg", import.meta.url).href;
  const gbFlag = new URL("../assets/flags-svg/gb.svg", import.meta.url).href;
  const inFlag = new URL("../assets/flags-svg/in.svg", import.meta.url).href;

  // Flag mapping for common country codes
  const flagMap: { [key: string]: string } = {
    us: usFlag,
    gb: gbFlag,
    uk: gbFlag, // Alternative key for UK
    in: inFlag,
  };

  const getFlagUrl = (key: string) => {
    return flagMap[key.toLowerCase()] || "";
  };

  const handleOptionClick = (option: DataSourceItem) => {
    if (!option.key) {
      console.error("Option key is undefined");
      return;
    }
    setSelectedOption(option);
    setIsOpen(false);
    props.updateFieldValue(option.key);
    props.triggerFieldChange(option.key);
  };

  /**
   * TODO
   * This select implementation with flags is not currently used, nor is it complete.
   * ie. it's styling incorrectly, it doesnt show all the flags etc.. etc..
   * When this is needed I suggest looking at the Phone.tsx implementation which also has flags
   * and doing similar/reusing.
   * Also make sure to implement props like disabled etc...
   */
  if (props.showFlags) {
    return (
      <div className="form-group">
        <label className="text-base">{props.label}</label>
        <div className="custom-select-container" ref={dropdownRef}>
          <div
            className={`custom-select ${isOpen ? "open" : ""}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <div
              className={`custom-select-trigger ${errorClasses}`}
              data-error={!!props.validatemessage}
            >
              {selectedOption && selectedOption.key ? (
                <div className="custom-option">
                  <img
                    src={getFlagUrl(selectedOption.key)}
                    alt={`${selectedOption.key} flag`}
                    className="flag-icon-inline"
                  />
                  <span>{selectedOption.value}</span>
                </div>
              ) : (
                <span className="placeholder">{props.placeholder}</span>
              )}
              <span className="dropdown-arrow">▼</span>
            </div>
            {isOpen && (
              <div className="custom-options">
                {props.options?.map((item) => (
                  <div
                    key={item.key}
                    className="custom-option"
                    onClick={() => handleOptionClick(item)}
                  >
                    <img
                      src={getFlagUrl(item.key!)}
                      alt={`${item.key} flag`}
                      className="flag-icon-inline"
                    />
                    <span>{item.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <FieldValidationMessage message={props.validatemessage} />
      </div>
    );
  }

  const selectClasses = getInputClasses(props.fullWidth, props.className);

  // Normal select without flags
  return (
    <div className="form-group">
      <label>{props.label}</label>
      <div ref={rootRef} className="relative">
        <select
          id={props.id}
          name={props.name}
          className={`${selectClasses} appearance-none`}
          onChange={(e) => {
            props.updateFieldValue(e.target.value);
            props.triggerFieldChange(e.target.value);
          }}
          value={props.value}
          data-error={!!props.validatemessage}
          data-populated={!!props.value}
          disabled={props.disabled}
        >
          <option value="">{props.placeholder}</option>
          {props.options?.map((item) => (
            <option key={item.key} value={item.key}>
              {item.value}
            </option>
          ))}
        </select>
        <img
          src={nonSpectreJourney(journeyId) ? ChevronDownIconDark : ChevronDownIcon}
          alt="dropdown arrow"
          className="absolute right-4 top-[40%] w-4 h-4 pointer-events-none"
        />
      </div>
      <FieldValidationMessage message={props.validatemessage} />
    </div>
  );
}

export default Select;
